import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  getActiveAutoFixRun,
  getAutoFixRun,
  listAutoFixRuns,
  startAutoFix,
  stopAutoFix,
  StartAutoFixParams,
  AuditRun,
} from "../services/audit-fix";

const ACTIVE_RUN_KEY = ["audit-fix-active"];
const runKey = (id: string) => ["audit-fix-run", id];

const POLLING_STATUSES: AuditRun["status"][] = ["PENDING", "RUNNING", "STOPPING"];

/** Checks for an active run on mount — used to re-attach to an in-progress run. */
export function useActiveAuditFixRun() {
  return useQuery({
    queryKey: ACTIVE_RUN_KEY,
    queryFn: getActiveAutoFixRun,
    staleTime: 0,
  });
}

/** Polls a specific run, auto-stops polling when the run reaches a terminal state. */
export function useAuditFixRun(runId: string | null, logsPage = 0) {
  return useQuery({
    queryKey: [...runKey(runId ?? ""), logsPage],
    queryFn: () => getAutoFixRun(runId!, logsPage),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status && POLLING_STATUSES.includes(status) ? 3000 : false;
    },
    staleTime: 0,
  });
}

export function useStartAutoFix() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: StartAutoFixParams) => startAutoFix(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_RUN_KEY });
      toast({ title: "Auto-fix started", description: `Run ID: ${data.run_id}` });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to start auto-fix",
        description: err.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });
}

/** Fetches the paginated list of all past runs for the history view. */
export function useAutoFixRuns(page = 0) {
  return useQuery({
    queryKey: ["audit-fix-runs", page],
    queryFn: () => listAutoFixRuns(page),
    staleTime: 1000 * 30,
  });
}

export function useStopAutoFix() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (runId: string) => stopAutoFix(runId),
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: runKey(runId) });
      queryClient.invalidateQueries({ queryKey: ACTIVE_RUN_KEY });
      toast({ title: "Stop signal sent", description: "The current group will finish then the run will halt." });
    },
    onError: () => {
      toast({ title: "Failed to stop run", variant: "destructive" });
    },
  });
}
